import { useEffect, useState } from 'react'
import { UserPlus, X, Crown, Loader2 } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import Modal from '../ui/Modal'
import {
  AssignmentRoleLabel,
  type AssignmentRole,
  type CaseAssignmentDto,
  type UserListItem,
} from '@toga/shared'

interface Props {
  caseId: string
  canManage: boolean
  assignments: CaseAssignmentDto[]
  onChanged: () => void
}

export default function AssignmentsPanel({ caseId, canManage, assignments, onChanged }: Props) {
  const [adding, setAdding] = useState(false)

  const onRemove = async (a: CaseAssignmentDto) => {
    if (!confirm(`¿Quitar a ${a.userFullName} del expediente?`)) return
    try {
      await api.delete(`/cases/${caseId}/assignments/${a.userId}`)
      onChanged()
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        alert(body?.error ?? err.message)
      } else {
        alert('Error de red')
      }
    }
  }

  return (
    <div className="surface p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="t-primary font-semibold flex items-center gap-2">Equipo</h3>
        {canManage && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="px-2.5 py-1 rounded-lg text-xs font-medium text-toga-200 bg-toga-500/15 hover:bg-toga-500/25 flex items-center gap-1"
          >
            <UserPlus size={14} /> Añadir
          </button>
        )}
      </div>

      {assignments.length === 0 ? (
        <p className="t-muted text-sm">Sin abogados asignados.</p>
      ) : (
        <ul className="space-y-1.5">
          {assignments.map((a) => (
            <li
              key={a.id}
              className="flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-hover/50"
            >
              <span className="w-8 h-8 rounded-full grid place-items-center bg-gradient-to-br from-toga-600 to-toga-800 text-white text-xs font-semibold">
                {a.userFullName
                  .split(/\s+/)
                  .slice(0, 2)
                  .map((p) => p[0]?.toUpperCase() ?? '')
                  .join('')}
              </span>
              <div className="flex-1 min-w-0">
                <div className="t-primary text-sm font-medium truncate flex items-center gap-1.5">
                  {a.userFullName}
                  {a.role === 'LEAD' && <Crown size={12} className="text-amber-300" />}
                </div>
                <div className="t-muted text-xs">{AssignmentRoleLabel[a.role]}</div>
              </div>
              {canManage && (
                <button
                  type="button"
                  onClick={() => onRemove(a)}
                  className="w-7 h-7 grid place-items-center rounded-lg hover:bg-rose-500/10 t-muted hover:text-rose-400"
                  title="Quitar"
                >
                  <X size={14} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {adding && (
        <AddAssignmentModal
          caseId={caseId}
          excludeUserIds={assignments.map((a) => a.userId)}
          onClose={() => setAdding(false)}
          onAdded={() => {
            setAdding(false)
            onChanged()
          }}
        />
      )}
    </div>
  )
}

function AddAssignmentModal({
  caseId,
  excludeUserIds,
  onClose,
  onAdded,
}: {
  caseId: string
  excludeUserIds: string[]
  onClose: () => void
  onAdded: () => void
}) {
  const [users, setUsers] = useState<UserListItem[] | null>(null)
  const [userId, setUserId] = useState('')
  const [role, setRole] = useState<AssignmentRole>('COLABORADOR')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    void (async () => {
      try {
        const { users } = await api.get<{ users: UserListItem[] }>('/users')
        const available = users.filter(
          (u) => u.isActive && u.role !== 'LECTURA' && !excludeUserIds.includes(u.id)
        )
        setUsers(available)
        if (available[0]) setUserId(available[0].id)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error al cargar usuarios')
      }
    })()
  }, [excludeUserIds])

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await api.post(`/cases/${caseId}/assignments`, { userId, role })
      onAdded()
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        setError(body?.error ?? err.message)
      } else {
        setError('Error de red')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal open onClose={onClose} title="Añadir miembro al equipo">
      {users === null ? (
        <div className="t-muted text-sm flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Cargando…
        </div>
      ) : users.length === 0 ? (
        <p className="t-muted text-sm">
          No hay más usuarios disponibles (todos están ya asignados o no tienen permisos).
        </p>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-xs t-muted mb-1.5">Usuario</label>
            <select
              required
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700/60 text-slate-100"
            >
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.fullName} · {u.role}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">Rol en el expediente</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as AssignmentRole)}
              className="w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700/60 text-slate-100"
            >
              <option value="LEAD">Responsable</option>
              <option value="COLABORADOR">Colaborador</option>
              <option value="PARALEGAL">Paralegal</option>
            </select>
          </div>
          {error && (
            <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm bg-slate-800/60 hover:bg-slate-800 t-secondary"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 disabled:opacity-50 flex items-center gap-2"
            >
              {submitting && <Loader2 size={14} className="animate-spin" />}
              Asignar
            </button>
          </div>
        </form>
      )}
    </Modal>
  )
}
